import { Link, useParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import JsonLd from "@/components/base/JsonLd";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleBody from "@/pages/article/components/ArticleBody";
import ArticleToc from "@/pages/article/components/ArticleToc";
import RelatedArticles from "@/pages/article/components/RelatedArticles";
import { articles } from "@/mocks/articles";
import { rankTotal } from "@/mocks/rankings";
import { formatFullDate, formatHeat } from "@/utils/format";

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = articles.find((item) => item.slug === slug);

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center px-4 py-24 text-center md:px-8">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-unknow-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这篇内容可能已经调整或下架
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            你可以回到每日合集列表，浏览当前正在更新的话题内容。
          </p>
          <Link
            to="/daily"
            className="mt-6 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line text-base"></i>
            </span>
            返回每日合集
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = articles
    .filter((item) => item.slug !== article.slug && item.categorySlug === article.categorySlug)
    .concat(articles.filter((item) => item.slug !== article.slug))
    .filter((item, index, arr) => arr.findIndex((x) => x.slug === item.slug) === index)
    .slice(0, 3);

  const tocItems = article.sections.map((section, index) => ({
    id: `section-${index}`,
    heading: section.heading,
  }));

  const hotList = rankTotal.filter((item) => item.slug !== article.slug).slice(0, 5);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    articleSection: article.category,
    keywords: article.tags.join(","),
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: "zh-CN",
    author: { "@type": "Organization", name: article.author },
    publisher: { "@type": "Organization", name: "瓜田日报" },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://dearyuan.cn/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://dearyuan.cn/" },
      {
        "@type": "ListItem",
        position: 2,
        name: article.category,
        item: `https://dearyuan.cn/daily?category=${article.categorySlug}`,
      },
      { "@type": "ListItem", position: 3, name: article.title },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${article.title}｜瓜田日报`}
        description={article.excerpt}
        keywords={`${article.tags.join(",")},${article.category},爆料吃瓜`}
        canonical={`/article/${article.slug}`}
        ogType="article"
      />
      <JsonLd data={articleSchema} />
      <JsonLd data={breadcrumbSchema} />

      <SiteLayout>
        <div className="border-b border-background-200/70 bg-background-100">
          <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumb items={[{ label: article.category, path: `/daily?category=${article.categorySlug}` }, { label: article.title }]} />
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <Link
                to={`/daily?category=${article.categorySlug}`}
                className="rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-800 transition-colors duration-200 hover:bg-secondary-200"
              >
                {article.category}
              </Link>
              <span>{formatFullDate(article.date)}</span>
              <span>阅读约 {article.readTime}</span>
              <span className="flex items-center gap-1">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-fire-line text-[12px] text-primary-500"></i>
                </span>
                热度 {formatHeat(article.heat)}
              </span>
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {article.excerpt}
            </p>
            <p className="mt-5 text-xs text-foreground-500">
              作者：{article.author} · 内容由编辑团队整理
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px]">
            <div>
              <div className="h-56 w-full overflow-hidden rounded-card bg-background-200 md:h-96">
                <img
                  src={article.cover}
                  alt={article.coverAlt}
                  title={article.title}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="mt-6">
                <ArticleBody article={article} />
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Link
                  to="/daily"
                  className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors duration-200 hover:text-primary-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-arrow-left-line text-base"></i>
                  </span>
                  返回每日合集列表
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">分享给同样在吃瓜的朋友</span>
                  <span className="flex gap-2">
                    {["ri-wechat-line", "ri-weibo-line", "ri-link"].map((icon) => (
                      <span
                        key={icon}
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-background-300/70 text-foreground-600 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
                      >
                        <i className={`${icon} text-base`}></i>
                      </span>
                    ))}
                  </span>
                </div>
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <div className="lg:sticky lg:top-28">
                <div className="flex flex-col gap-6">
                  <ArticleToc items={tocItems} />
                  <RelatedArticles items={related} />
                  <div className="rounded-card border border-background-200/80 bg-background-50 p-5">
                    <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-fire-line text-primary-500"></i>
                      </span>
                      热榜即时更新
                    </h2>
                    <ol className="mt-4 flex flex-col gap-3">
                      {hotList.map((item) => (
                        <li key={item.slug}>
                          <Link
                            to={`/article/${item.slug}`}
                            className="group flex items-start gap-2.5"
                          >
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-background-200 text-xs font-bold text-foreground-600">
                              {item.rank}
                            </span>
                            <span className="line-clamp-2 text-xs leading-snug text-foreground-700 transition-colors duration-200 group-hover:text-primary-700">
                              {item.title}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ol>
                    <Link
                      to="/ranking"
                      className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors duration-200 hover:text-primary-700"
                    >
                      查看完整榜单
                      <span className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className="ri-arrow-right-line text-[13px]"></i>
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </SiteLayout>
    </>
  );
}